import { format } from 'date-fns';
import { createSelector } from 'reselect';
import { filterTree } from '../utils/treeSync';
import { ViewModes } from '../viewModes';
import * as Utils from '../viewModes/utils';
import { getDataItemsForMedia } from './timelineDataCache';
import {
  AddressingEntity,
  ApplicationState,
  DateRangeNavigatorFunctions,
  IViewMode,
  MediaItem,
  PresetType,
  TreeItem,
  ViewModeType
} from './types';
import { getVisibleTree } from './visibleTreeCache';

const addressingRulesSelector = (state: ApplicationState) => state.addressing.addressingRules;
const startDateSelector = (state: ApplicationState) => state.timeline.startDate;
const endDateSelector = (state: ApplicationState) => state.timeline.endDate;
const viewModeTypeSelector = (state: ApplicationState) => state.timeline.viewModeType;
const presetTypeSelector = (state: ApplicationState) => state.timeline.preset;
const lastToggledSelector = (state: ApplicationState) => state.medias.lastToggled;

const groupsNormalizedSelector = (state: ApplicationState) => state.addressing.groups;
const sitesNormalizedSelector = (state: ApplicationState) => state.addressing.sites;
const playersNormalizedSelector = (state: ApplicationState) => state.addressing.players;

const selectedGroupSelector = (state: ApplicationState) => state.addressing.selectedGroup;
const selectedSiteSelector = (state: ApplicationState) => state.addressing.selectedSite;
const selectedPlayerSelector = (state: ApplicationState) => state.addressing.selectedPlayer;

const treeSelector = (state: ApplicationState) => state.medias.tree;
export const mediasNormalizedSelector = (state: ApplicationState) => state.medias.medias;

export const denormalizedMediasSelector = createSelector(
  [mediasNormalizedSelector],
  medias => Object.keys(medias).map(key => medias[key] as MediaItem)
);

export const groupsDenormalizedSelector = createSelector(
  [groupsNormalizedSelector],
  groups => Object.keys(groups).map(key => groups[key] as AddressingEntity)
);

export const sitesDenormalizedSelector = createSelector(
  [sitesNormalizedSelector],
  sites => Object.keys(sites).map(key => sites[key] as AddressingEntity)
);

export const playersDenormalizedSelector = createSelector(
  [playersNormalizedSelector],
  players => Object.keys(players).map(key => players[key] as AddressingEntity)
);

export const sitesInGroupSelector = createSelector(
  [groupsNormalizedSelector, sitesNormalizedSelector, sitesDenormalizedSelector, selectedGroupSelector],
  (groups, sites, sitesDenormed, selectedGroup) =>
    selectedGroup ? groups[selectedGroup].children.map(c => sites[c]) : sitesDenormed
);

export const playersInSiteSelector = createSelector(
  [
    groupsNormalizedSelector,
    sitesNormalizedSelector,
    playersNormalizedSelector,
    playersDenormalizedSelector,
    selectedSiteSelector,
    selectedGroupSelector
  ],
  (groups, sites, players, playersDenormed, selectedSite, selectedGroup) =>
    selectedSite
      ? sites[selectedSite].children.map(c => players[c])
      : selectedGroup
      ? groups[selectedGroup].children
          .map(siteId => sites[siteId])
          .reduce<AddressingEntity[]>((acc, site) => acc.concat(site.children.map(playerId => players[playerId])), [])
      : playersDenormed
);

export const columnsSelector = createSelector(
  [startDateSelector, endDateSelector, viewModeTypeSelector],
  (startDate: Date, endDate: Date, viewModeType: ViewModeType) => {
    const viewM = ViewModes[viewModeType] as IViewMode;
    if (!viewM) {
      return [];
    }
    viewM.computeColumns(startDate, endDate);
    return viewM.columns;
  }
);

export const filterTreeSelector = createSelector(
  [treeSelector, selectedPlayerSelector, addressingRulesSelector, lastToggledSelector],
  (tree, selectedPlayer, rules, lastToggled) => {
    const visibleTree = getVisibleTree(tree, lastToggled);

    if (!selectedPlayer || !rules[selectedPlayer]) {
      return [...visibleTree];
    }
    return filterTree(visibleTree, rules[selectedPlayer].denies);
  }
);

export const filterButtonContentSelector = createSelector(
  [selectedPlayerSelector, playersNormalizedSelector],
  (selectedPlayer, players) => ({
    active: selectedPlayer !== 0,
    text: selectedPlayer ? players[selectedPlayer].name : 'NO FILTER'
  })
);

export const timelineDataSelector = createSelector(
  [startDateSelector, endDateSelector, viewModeTypeSelector, filterTreeSelector, mediasNormalizedSelector],
  (
    startDate: Date,
    endDate: Date,
    viewModeType: ViewModeType,
    tree: TreeItem[],
    medias: { [id: number]: MediaItem }
  ) => {
    const viewM = ViewModes[viewModeType] as IViewMode;
    if (!viewM || Object.keys(medias).length === 0) {
      return [];
    }
    return tree.map(ti => getDataItemsForMedia(startDate, endDate, viewM, ti, medias[ti.id]));
  }
);

export const getDateRangeNavigatorFunctions = createSelector(
  [presetTypeSelector],
  presetType => {
    let fns: DateRangeNavigatorFunctions;
    switch (presetType) {
      case PresetType.Day:
        fns = {
          previous: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.previousDay(startDate),
          next: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.nextDay(startDate),
          info: (startDate: Date, _: Date): string => format(startDate, 'DD/MM/YYYY')
        };
        break;
      case PresetType.Week:
        fns = {
          previous: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.previousWeek(startDate),
          next: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.nextWeek(startDate),
          info: (startDate: Date, _: Date): string =>
            `Week ${Utils.numberOfWeek(startDate).toString()}, ${
              Utils.monthNames[startDate.getMonth()]
            } ${startDate.getFullYear()}`
        };
        break;
      case PresetType.Month:
        fns = {
          previous: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.previousMonth(startDate),
          next: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.nextMonth(startDate),
          info: (startDate: Date, _: Date): string =>
            `${Utils.nameOfTheMonth(startDate)} ${startDate.getFullYear().toString()}`
        };
        break;
      case PresetType.Year:
        fns = {
          previous: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.previousYear(startDate),
          next: (startDate: Date, _: Date): { startDate: Date; endDate: Date } => Utils.nextYear(startDate),
          info: (startDate: Date, _: Date): string => startDate.getFullYear().toString()
        };
        break;
      default:
        fns = {
          previous: (startDate: Date, endDate: Date): { startDate: Date; endDate: Date } => ({ startDate, endDate }),
          next: (startDate: Date, endDate: Date): { startDate: Date; endDate: Date } => ({ startDate, endDate }),
          info: (startDate: Date, endDate: Date): string => ''
        };
    }
    return fns;
  }
);



// WEBPACK FOOTER //
// ./src/store/selectors.ts